import { Card, Flex, Heading, Text, Box, Button, Separator, TextField, Badge, Dialog, IconButton, Avatar, Grid } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../config/axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { passwordRules } from '../../Schema/index.js'; // Assuming this path is correct
import { createPortal } from 'react-dom';
import { MdOutlinePersonOutline, MdOutlineVpnKey, MdOutlineClose, MdOutlineBusiness, MdOutlineEmail, MdOutlineBadge, MdOutlineLocationOn, MdOutlineSupervisorAccount } from 'react-icons/md';

// Validation schema for password change form
const passwordSchema = Yup.object({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .matches(passwordRules, { message: 'Please create a stronger password (at least one lowercase, uppercase, digit, and special character)' })
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your new password'),
});

// New greenish-teal color scheme
const tealGradientStart = '#2dd4bf'; // Brighter teal
const tealGradientEnd = '#10b981';   // Darker green/teal

// Modern CSS styles with glassmorphism and animations
const styles = `
  .profile-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(12px);
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .profile-popup-content {
    width: 95vw;
    max-width: 800px;
    max-height: 90vh;
    background: #ffffff;
    border-radius: 32px;
    box-shadow: 
      0 30px 60px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    position: relative;
    animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  
  .profile-header {
    background: linear-gradient(135deg, ${tealGradientStart} 0%, ${tealGradientEnd} 100%);
    height: 130px;
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 20px;
  }
  
  .profile-header::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 40 L40 0 M-10 30 L30 -10 M10 50 L50 10' stroke='white' stroke-width='1' fill='none' opacity='0.05'/%3E%3C/svg%3E");
    opacity: 0.5;
  }
  
  .profile-avatar-container {
    position: relative;
    margin-top: -45px; /* Offset for overlap */
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  }

  .profile-avatar {
    width: 95px;
    height: 95px;
    border-radius: 50%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 15px 35px -5px rgba(0, 0, 0, 0.15);
    border: 4px solid #ffffff;
    transition: transform 0.3s ease;
  }
  
  .profile-avatar:hover {
    transform: scale(1.05);
  }
  
  .profile-body {
    padding: 0 24px 24px;
    max-height: calc(90vh - 180px); 
    overflow-y: auto;
    overflow-x: hidden;
  }
  
  .profile-main-info {
    text-align: center;
    margin-bottom: 24px;
  }

  .profile-section {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 20px;
  }
  
  .section-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    gap: 16px;
  }

  .profile-field {
    padding: 12px 16px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  }
  
  .profile-field:hover {
    border-color: ${tealGradientStart};
    box-shadow: 0 4px 12px rgba(45, 212, 191, 0.1);
    transform: translateY(-1px);
  }
  
  .field-icon {
    width: 18px;
    height: 18px;
    color: ${tealGradientEnd};
    opacity: 0.7;
  }
  
  .close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    z-index: 100;
  }
  
  .close-button:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.1);
  }
  
  .modern-button {
    background: linear-gradient(135deg, ${tealGradientStart}, ${tealGradientEnd});
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    color: white;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 16px -4px rgba(20, 184, 166, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-shrink: 0;
  }
  
  .modern-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -4px rgba(20, 184, 166, 0.35);
  }
  
  .modern-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #94a3b8;
    box-shadow: none;
  }

  .status-badge {
    padding: 5px 12px;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: inline-block;
  }
  
  .password-form-container {
    background: #ffffff;
    border: 1px solid ${tealGradientStart}25;
    border-radius: 12px;
    padding: 20px;
    margin-top: 8px;
    animation: fadeIn 0.3s ease-out;
  }

  .rt-TextFieldRoot {
    border-radius: 4px; /* Rectangular style */
    transition: all 0.2s ease;
  }
  
  .input-error .rt-TextFieldInput {
    border-color: #ef4444;
    box-shadow: 0 0 0 1px #ef4444;
  }

  .error-container {
    min-height: 20px;
    margin-top: 4px;
    display: flex;
    align-items: center;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(15px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .scrollbar-hide::-webkit-scrollbar { width: 6px; }
  .scrollbar-hide::-webkit-scrollbar-track { background: transparent; }
  .scrollbar-hide::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
  .scrollbar-hide { scrollbar-width: thin; scrollbar-color: #e2e8f0 transparent; }
`;

const ProfilePopup = ({ triggerButton = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const response = await axiosInstance.post('/Account/update-password', {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });

        if (response.data.result) {
          toast.success('Password changed successfully!', {
            position: "top-right",
            style: {
              background: `linear-gradient(145deg, ${tealGradientStart}, ${tealGradientEnd})`,
              color: 'white',
              borderRadius: '12px'
            }
          });
          setIsChangingPassword(false);
          resetForm();
        } else {
          toast.error(response.data.errorResponse?.message || 'Failed to change password');
        }
      } catch (error) {
        console.error('Error changing password:', error);
        toast.error(error.response?.data?.errorResponse?.message || 'Error changing password');
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isOpen && !profileData) {
      fetchProfileData();
    }
  }, [isOpen, profileData]); // Added profileData back to deps for specific scenario if needed for refetch, carefully. Usually fetch only on open if not exists.

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/User/get-profile');
      if (response.data.result?.userGetList?.length > 0) {
        setProfileData(response.data.result.userGetList[0]);
      } else {
        setProfileData({});
        toast.info('Profile data not found.');
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Error loading profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const renderProfileField = (label, value, icon) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    return (
      <div className="profile-field">
        {icon && <span className="field-icon">{icon}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="2" weight="medium" style={{ color: '#64748b', display: 'block', marginBottom: '4px' }}>
            {label}
          </Text>
          <Text size="3" weight="bold" style={{ color: '#1e293b', wordBreak: 'break-word' }}>
            {value}
          </Text>
        </div>
      </div>
    );
  };

  const getFullName = (firstName, lastName) => {
    const f = firstName?.trim() || '';
    const l = lastName?.trim() || '';
    if (f === l) return f;
    return `${f} ${l}`.trim();
  };

  const getInitials = (firstName, lastName) => {
    const f = firstName?.trim() || '';
    const l = lastName?.trim() || '';
    if (f === l && f) {
      const parts = f.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return f.charAt(0).toUpperCase();
    }
    return `${f.charAt(0) || ''}${l.charAt(0) || ''}`.toUpperCase();
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsChangingPassword(false);
    formik.resetForm();
  };

  const popupContent = isOpen ? (
    <div className="profile-popup-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="profile-popup-content">
        <style>{styles}</style>
        <ToastContainer position="top-right" autoClose={3000} containerId="profilePopupToast" />

        <div className="profile-header">
          <button className="close-button" onClick={handleClose}>
            <MdOutlineClose size={20} />
          </button>
        </div>

        <div className="profile-avatar-container">
          <div className="profile-avatar">
            {profileData && (profileData.firstName || profileData.lastName) ? (
              <Text size="7" weight="bold" style={{ color: tealGradientStart }}>
                {getInitials(profileData.firstName, profileData.lastName)}
              </Text>
            ) : (
              <MdOutlinePersonOutline size={50} style={{ color: tealGradientStart }} />
            )}
          </div>

          <div className="profile-main-info">
            <Heading size="6" style={{ color: '#0f172a', fontWeight: '800', marginBottom: '4px' }}>
              {profileData ? getFullName(profileData.firstName, profileData.lastName) || 'User Profile' : 'User Profile'}
            </Heading>
            <Flex gap="3" align="center" justify="center">
              {profileData?.role && (
                <Text size="2" weight="medium" style={{ color: '#64748b' }}>
                  {profileData.role}
                </Text>
              )}
              {profileData?.hasOwnProperty('isActive') && (
                <span className="status-badge" style={{
                  backgroundColor: profileData.isActive ? `${tealGradientEnd}15` : '#fee2e2',
                  color: profileData.isActive ? tealGradientEnd : '#ef4444',
                }}>
                  {profileData.isActive ? '• Active' : '• Inactive'}
                </span>
              )}
            </Flex>
          </div>
        </div>

        <div className="profile-body scrollbar-hide">
          {isLoading ? (
            <Flex height="200px" justify="center" align="center">
              <Text size="3" style={{ color: '#64748b' }}>Loading profile data...</Text>
            </Flex>
          ) : (
            <>
              {profileData && Object.keys(profileData).length > 0 && (
                <div className="profile-section">
                  <div className="section-title-row">
                    <Heading size="4" style={{ color: '#0f172a', fontWeight: '800' }}>
                      {isChangingPassword ? 'Update Password' : 'Personal Information'}
                    </Heading>
                    <button
                      className="modern-button"
                      onClick={() => {
                        if (isChangingPassword) {
                          formik.resetForm();
                        }
                        setIsChangingPassword(!isChangingPassword);
                      }}
                      style={{
                        ...(isChangingPassword
                          ? { background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 10px 20px -5px rgba(239, 68, 68, 0.3)' }
                          : {}),
                        padding: '8px 20px',
                        fontSize: '13px'
                      }}
                    >
                      {isChangingPassword ? 'Cancel' : 'Change Password'}
                    </button>
                  </div>

                  {isChangingPassword ? (
                    <div className="password-form-container">
                      <Flex direction="column" gap="4">
                        <form onSubmit={formik.handleSubmit}>
                          <Flex direction="column" gap="3">
                            <label className="w-full">
                              <Text as="div" size="2" mb="1" weight="bold">Current Password</Text>
                              <TextField.Root
                                id="currentPassword"
                                name="currentPassword"
                                type="password"
                                placeholder="••••••••"
                                {...formik.getFieldProps('currentPassword')}
                                className={formik.touched.currentPassword && formik.errors.currentPassword ? "input-error" : ""}
                              />
                              <div className="error-container">
                                {formik.touched.currentPassword && formik.errors.currentPassword && (
                                  <Text size="1" color="red" display="block">{formik.errors.currentPassword}</Text>
                                )}
                              </div>
                            </label>

                            <label className="w-full">
                              <Text as="div" size="2" mb="1" weight="bold">New Password</Text>
                              <TextField.Root
                                id="newPassword"
                                name="newPassword"
                                type="password"
                                placeholder="••••••••"
                                {...formik.getFieldProps('newPassword')}
                                className={formik.touched.newPassword && formik.errors.newPassword ? "input-error" : ""}
                              />
                              <div className="error-container">
                                {formik.touched.newPassword && formik.errors.newPassword && (
                                  <Text size="1" color="red" display="block">{formik.errors.newPassword}</Text>
                                )}
                              </div>
                            </label>

                            <label className="w-full">
                              <Text as="div" size="2" mb="1" weight="bold">Confirm Password</Text>
                              <TextField.Root
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                placeholder="••••••••"
                                {...formik.getFieldProps('confirmPassword')}
                                className={formik.touched.confirmPassword && formik.errors.confirmPassword ? "input-error" : ""}
                              />
                              <div className="error-container">
                                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                  <Text size="1" color="red" display="block">{formik.errors.confirmPassword}</Text>
                                )}
                              </div>
                            </label>

                            <Flex gap="3" mt="2" justify="end">
                              <Button
                                type="submit"
                                className="modern-button"
                                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                              >
                                {formik.isSubmitting ? 'Updating...' : 'Save New Password'}
                              </Button>
                            </Flex>
                          </Flex>
                        </form>
                      </Flex>
                    </div>
                  ) : (
                    <Grid columns={{ initial: '1', sm: '2' }} gap="4" width="100%">
                      {renderProfileField('Full Name', getFullName(profileData.firstName, profileData.lastName), <MdOutlinePersonOutline />)}
                      {renderProfileField('Employee ID', profileData.employeeId, <MdOutlineBadge />)}
                      {renderProfileField('Email Address', profileData.email, <MdOutlineEmail />)}
                      {renderProfileField('Department', profileData.depId, <MdOutlineBusiness />)}
                      {renderProfileField('Username', profileData.userName, <MdOutlineBadge />)}
                      {renderProfileField('Unit', profileData.unitName, <MdOutlineBusiness />)}
                      {renderProfileField('Department Location', profileData.depLocation, <MdOutlineLocationOn />)}
                      {renderProfileField('Supervisor', profileData.supervisor, <MdOutlineSupervisorAccount />)}
                    </Grid>
                  )}
                </div>
              )}
              {profileData && Object.keys(profileData).length === 0 && !isLoading && (
                <div className="profile-section" style={{ textAlign: 'center' }}>
                  <Text>No profile information available.</Text>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {triggerButton ? (
        <div onClick={() => { setIsOpen(true); if (!profileData && !isLoading) fetchProfileData(); }}>
          {triggerButton}
        </div>
      ) : (
        <Button
          onClick={() => { setIsOpen(true); if (!profileData && !isLoading) fetchProfileData(); }}
          className="modern-button" // This will now use the new teal gradient
          size="2"
        >
          <MdOutlinePersonOutline size={18} /> {/* Icon within modern-button gap works */}
          View Profile
        </Button>
      )}

      {isOpen && createPortal(popupContent, document.body)}
    </>
  );
};

export default ProfilePopup;